/**
 * ExperiencesSelection Component
 *
 * Character Creation Step 7: Create two player-defined experiences
 * Each experience provides +2 bonus when applicable
 *
 * Features:
 * - Two input fields with real-time validation
 * - Character counter (0-40 chars)
 * - Category selector with example display
 * - Click-to-fill from examples
 * - Duplicate detection
 * - Error/Warning/Success states
 */

import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import {
  validateExperience,
  checkDuplicates,
  getCharCounterColor,
  MAX_EXPERIENCE_LENGTH,
  ExperienceValidation,
} from '../../utils/experienceValidation';
import {
  EXPERIENCE_CATEGORIES,
  getCategoryIds,
  getCategoryById,
  getRandomExamples,
  ExperienceExample,
} from '../../data/experienceExamples';
import styles from './ExperiencesSelection.module.css';

interface ExperiencesSelectionProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

/**
 * ExperiencesSelection component for character creation
 */
const ExperiencesSelection: React.FC<ExperiencesSelectionProps> = ({
  characterData,
  onDataChange,
}) => {
  // Local state for experience inputs
  const [experience1, setExperience1] = useState('');
  const [experience2, setExperience2] = useState('');

  // Track which fields have been touched (user interacted)
  const [touched1, setTouched1] = useState(false);
  const [touched2, setTouched2] = useState(false);

  // Validation states
  const [validation1, setValidation1] = useState<ExperienceValidation>({
    isValid: true,
    severity: 'success',
    message: '',
  });
  const [validation2, setValidation2] = useState<ExperienceValidation>({
    isValid: true,
    severity: 'success',
    message: '',
  });
  const [duplicateValidation, setDuplicateValidation] = useState<ExperienceValidation>({
    isValid: true,
    severity: 'success',
    message: 'No duplicates',
  });

  // Example display state
  const [selectedCategory, setSelectedCategory] = useState('backgrounds');
  const [displayedExamples, setDisplayedExamples] = useState<ExperienceExample[]>([]);

  // Initialize from characterData if experiences already exist
  useEffect(() => {
    if (characterData.experiences && characterData.experiences.length >= 2) {
      const exp1 = characterData.experiences[0].name;
      const exp2 = characterData.experiences[1].name;
      setExperience1(exp1);
      setExperience2(exp2);
      updateValidation(exp1, exp2);
    } else {
      // Load initial examples
      loadRandomExamples(selectedCategory);
    }
  }, []);

  /**
   * Load random examples from selected category
   */
  const loadRandomExamples = (categoryId: string) => {
    const examples = getRandomExamples(categoryId, 6);
    setDisplayedExamples(examples);
  };

  /**
   * Handle category change
   */
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    loadRandomExamples(categoryId);
  };

  /**
   * Shuffle examples in current category
   */
  const handleShuffleExamples = () => {
    loadRandomExamples(selectedCategory);
  };

  /**
   * Update all validations
   */
  const updateValidation = (exp1: string, exp2: string) => {
    const val1 = validateExperience(exp1);
    const val2 = validateExperience(exp2);
    const dupCheck = checkDuplicates(exp1, exp2);

    setValidation1(val1);
    setValidation2(val2);
    setDuplicateValidation(dupCheck);
  };

  /**
   * Handle experience 1 input change
   */
  const handleExperience1Change = (value: string) => {
    // Enforce max length
    const trimmed = value.slice(0, MAX_EXPERIENCE_LENGTH);
    setExperience1(trimmed);
    updateValidation(trimmed, experience2);
    updateCharacterData(trimmed, experience2);
  };

  /**
   * Handle experience 2 input change
   */
  const handleExperience2Change = (value: string) => {
    // Enforce max length
    const trimmed = value.slice(0, MAX_EXPERIENCE_LENGTH);
    setExperience2(trimmed);
    updateValidation(experience1, trimmed);
    updateCharacterData(experience1, trimmed);
  };

  /**
   * Update character data with current experiences
   */
  const updateCharacterData = (exp1: string, exp2: string) => {
    // Only update if both are non-empty
    if (exp1.trim() && exp2.trim()) {
      onDataChange({
        experiences: [
          {
            id: crypto.randomUUID(),
            name: exp1.trim(),
            bonus: 2,
            createdAtLevel: 1,
          },
          {
            id: crypto.randomUUID(),
            name: exp2.trim(),
            bonus: 2,
            createdAtLevel: 1,
          },
        ],
      });
    } else if (exp1.trim() || exp2.trim()) {
      // One is filled - store partial data but won't allow proceed
      const experiences = [];
      if (exp1.trim()) {
        experiences.push({
          id: crypto.randomUUID(),
          name: exp1.trim(),
          bonus: 2,
          createdAtLevel: 1,
        });
      }
      if (exp2.trim()) {
        experiences.push({
          id: crypto.randomUUID(),
          name: exp2.trim(),
          bonus: 2,
          createdAtLevel: 1,
        });
      }
      onDataChange({ experiences });
    } else {
      // Both empty
      onDataChange({ experiences: [] });
    }
  };

  /**
   * Click an example to fill input field
   */
  const handleExampleClick = (exampleName: string, targetField: 1 | 2) => {
    if (targetField === 1) {
      handleExperience1Change(exampleName);
    } else {
      handleExperience2Change(exampleName);
    }
  };

  /**
   * Get validation message component
   */
  const renderValidationMessage = (validation: ExperienceValidation, fieldLabel: string) => {
    if (validation.severity === 'success') {
      return null; // Don't show success messages for individual fields
    }

    const className =
      validation.severity === 'error'
        ? styles.validationError
        : styles.validationWarning;

    const icon = validation.severity === 'error' ? '❌' : '⚠️';

    return (
      <div className={className} role="alert" aria-live="polite">
        {icon} {validation.message}
      </div>
    );
  };

  /**
   * Get character counter class
   */
  const getCharCounterClass = (length: number): string => {
    const color = getCharCounterColor(length);
    if (color === 'error') return styles.charCounterError;
    if (color === 'warning') return styles.charCounterWarning;
    return styles.charCounterNormal;
  };

  // Get category data
  const currentCategory = getCategoryById(selectedCategory);
  const categoryIds = getCategoryIds();

  // Check if both experiences are valid (for success message)
  const allValid =
    validation1.severity !== 'error' &&
    validation2.severity !== 'error' &&
    duplicateValidation.severity !== 'error' &&
    experience1.trim().length > 0 &&
    experience2.trim().length > 0;

  return (
    <div className={styles.experiencesSelection}>
      <div className={styles.header}>
        <h2>Define Your Experiences</h2>
        <p>
          Create two experiences that represent your character's background, skills, or
          personality. Each experience grants a +2 bonus when relevant to your actions.
        </p>
        <p className={styles.hint}>
          Examples: "Former Soldier", "Silver Tongue", "Survivalist", "I've Got Your Back"
        </p>
      </div>

      {/* Experience Input 1 */}
      <div className={styles.experienceInput}>
        <label htmlFor="experience-1">
          <strong>Experience 1</strong>
          <span className={styles.required}> *</span>
        </label>
        <div className={styles.inputWrapper}>
          <input
            id="experience-1"
            type="text"
            value={experience1}
            onChange={(e) => handleExperience1Change(e.target.value)}
            onBlur={() => setTouched1(true)}
            placeholder="Enter your first experience..."
            maxLength={MAX_EXPERIENCE_LENGTH}
            className={
              touched1 && validation1.severity === 'error'
                ? styles.inputError
                : touched1 && validation1.severity === 'warning'
                ? styles.inputWarning
                : ''
            }
            aria-describedby="experience-1-validation experience-1-counter"
          />
          <div
            id="experience-1-counter"
            className={`${styles.charCounter} ${getCharCounterClass(experience1.length)}`}
          >
            {experience1.length}/{MAX_EXPERIENCE_LENGTH}
          </div>
        </div>
        <div id="experience-1-validation">
          {touched1 && renderValidationMessage(validation1, 'Experience 1')}
        </div>
      </div>

      {/* Experience Input 2 */}
      <div className={styles.experienceInput}>
        <label htmlFor="experience-2">
          <strong>Experience 2</strong>
          <span className={styles.required}> *</span>
        </label>
        <div className={styles.inputWrapper}>
          <input
            id="experience-2"
            type="text"
            value={experience2}
            onChange={(e) => handleExperience2Change(e.target.value)}
            onBlur={() => setTouched2(true)}
            placeholder="Enter your second experience..."
            maxLength={MAX_EXPERIENCE_LENGTH}
            className={
              touched2 && validation2.severity === 'error'
                ? styles.inputError
                : touched2 && validation2.severity === 'warning'
                ? styles.inputWarning
                : ''
            }
            aria-describedby="experience-2-validation experience-2-counter"
          />
          <div
            id="experience-2-counter"
            className={`${styles.charCounter} ${getCharCounterClass(experience2.length)}`}
          >
            {experience2.length}/{MAX_EXPERIENCE_LENGTH}
          </div>
        </div>
        <div id="experience-2-validation">
          {touched2 && renderValidationMessage(validation2, 'Experience 2')}
        </div>
      </div>

      {/* Duplicate Check */}
      {duplicateValidation.severity === 'error' && (
        <div className={styles.validationError} role="alert" aria-live="polite">
          ❌ {duplicateValidation.message}
        </div>
      )}

      {/* Example Selector */}
      <div className={styles.examplesSection}>
        <div className={styles.examplesHeader}>
          <h3>Need Inspiration?</h3>
          <p>Browse examples by category or click any example to use it</p>
        </div>

        <div className={styles.categorySelector}>
          <label htmlFor="category-select">Category:</label>
          <select
            id="category-select"
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className={styles.categorySelect}
          >
            {categoryIds.map((categoryId) => {
              const category = getCategoryById(categoryId);
              return (
                <option key={categoryId} value={categoryId}>
                  {category?.name}
                </option>
              );
            })}
          </select>
          <button
            type="button"
            onClick={handleShuffleExamples}
            className={styles.shuffleButton}
            aria-label="Shuffle examples"
            title="Show different examples"
          >
            🔀 Shuffle
          </button>
        </div>

        {currentCategory && (
          <div className={styles.categoryDescription}>
            <p>{currentCategory.description}</p>
          </div>
        )}

        <div className={styles.examplesList}>
          {displayedExamples.map((example, index) => (
            <div key={index} className={styles.exampleCard}>
              <div className={styles.exampleHeader}>
                <strong>{example.name}</strong>
              </div>
              {example.description && (
                <p className={styles.exampleDescription}>{example.description}</p>
              )}
              <div className={styles.exampleActions}>
                <button
                  type="button"
                  onClick={() => handleExampleClick(example.name, 1)}
                  className={styles.exampleButton}
                  aria-label={`Use "${example.name}" for Experience 1`}
                >
                  Use for #1
                </button>
                <button
                  type="button"
                  onClick={() => handleExampleClick(example.name, 2)}
                  className={styles.exampleButton}
                  aria-label={`Use "${example.name}" for Experience 2`}
                >
                  Use for #2
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExperiencesSelection;
